import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminJobsOverview, useAdminRecentJobs } from "@/hooks/use-queries";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Briefcase, FolderOpen, CheckCircle2, UserCheck, ChevronDown, ChevronUp, Clock3, Activity, Gavel } from "lucide-react";

const JOB_STATUS_COLORS: Record<string, string> = {
  OPEN: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  ASSIGNED: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  IN_PROGRESS: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
  IN_REVIEW: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  COMPLETED: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  DISPUTED: "bg-red-500/10 text-red-600 border-red-500/20",
  CANCELLED: "bg-muted text-muted-foreground border-border",
};

function StatCard({
  icon: Icon,
  iconColor,
  label,
  value,
  sub,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card className="glass-panel">
      <CardContent className="p-5">
        <div className="mb-3 flex items-start justify-between">
          <div className={`rounded-lg bg-surface-subtle p-2 ${iconColor}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="mb-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-2xl font-display font-bold">{value}</p>
        {sub && <p className="mt-0.5 text-[10px] font-mono text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function AdminJobs() {
  const { data: overview, isLoading: overviewLoading } = useAdminJobsOverview();
  const { data: jobs, isLoading: jobsLoading } = useAdminRecentJobs(50);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-1 text-sm font-mono uppercase tracking-[0.2em] text-primary">Admin</h2>
        <h1 className="text-3xl font-display font-bold">Jobs</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Review recent job postings and the action timeline on each job.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {overviewLoading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
        ) : (
          <>
            <StatCard icon={Briefcase} iconColor="text-blue-400" label="Total Jobs" value={overview?.total ?? "—"} />
            <StatCard icon={FolderOpen} iconColor="text-cyan-400" label="Open" value={overview?.open ?? "—"} />
            <StatCard icon={UserCheck} iconColor="text-amber-400" label="Assigned" value={overview?.assigned ?? "—"} />
            <StatCard icon={CheckCircle2} iconColor="text-emerald-400" label="Completed" value={overview?.completed ?? "—"} />
          </>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
          <Briefcase className="h-3.5 w-3.5" /> Recent Job Postings
        </h3>
        <Card className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-divider">
                  <th className="w-8 p-3"></th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Job</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Poster</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Budget</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Bids</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Actions</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Created</th>
                </tr>
              </thead>
              <tbody>
                {jobsLoading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} className="border-b border-divider/60">
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                      ))}
                    </tr>
                  ))
                ) : !jobs?.length ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      <div className="rounded-lg border-2 border-dashed border-divider py-8">
                        No jobs found.
                      </div>
                    </td>
                  </tr>
                ) : (
                  jobs.map((job: any) => {
                    const isExpanded = expandedId === job.id;
                    return (
                      <>
                        <tr
                          key={job.id}
                          className="cursor-pointer border-b border-divider/60 transition-colors hover:bg-surface-subtle/60"
                          onClick={() => setExpandedId(isExpanded ? null : job.id)}
                        >
                          <td className="p-3 text-center">
                            <button className="text-muted-foreground hover:text-foreground">
                              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          </td>
                          <td className="p-3">
                            <div className="min-w-[220px]">
                              <Link
                                to={`/jobs/${job.id}`}
                                className="block truncate font-medium text-primary hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {job.title}
                              </Link>
                              <p className="truncate text-[10px] text-muted-foreground">{job.templateId}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="min-w-[180px]">
                              <p className="truncate font-medium">{job.poster?.displayName || "—"}</p>
                              <p className="truncate text-[10px] text-muted-foreground">{job.poster?.email || "—"}</p>
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge
                              variant="outline"
                              className={`text-[9px] py-0 ${JOB_STATUS_COLORS[job.status] ?? "bg-surface-subtle text-muted-foreground border-divider"}`}
                            >
                              {job.status}
                            </Badge>
                          </td>
                          <td className="whitespace-nowrap p-3 font-semibold">{Number(job.budgetUsdc).toFixed(2)} USDC</td>
                          <td className="p-3">{job._count?.bids ?? 0}</td>
                          <td className="p-3">{job._count?.events ?? 0}</td>
                          <td className="whitespace-nowrap p-3 text-muted-foreground">
                            {new Date(job.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${job.id}-events`} className="border-b border-divider/60 bg-surface-subtle/60">
                            <td colSpan={8} className="p-4">
                              <div className="space-y-4">
                                <div className="grid gap-3 sm:grid-cols-3">
                                  <div className="rounded-lg border border-divider bg-surface-subtle p-3">
                                    <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Assigned Agent</p>
                                    <p className="text-sm font-medium">{job.agent?.name || "Unassigned"}</p>
                                  </div>
                                  <div className="rounded-lg border border-divider bg-surface-subtle p-3">
                                    <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Last Updated</p>
                                    <p className="text-sm font-medium">{new Date(job.updatedAt).toLocaleString()}</p>
                                  </div>
                                  <div className="rounded-lg border border-divider bg-surface-subtle p-3">
                                    <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Completed</p>
                                    <p className="text-sm font-medium">{job.completedAt ? new Date(job.completedAt).toLocaleString() : "Not completed"}</p>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <h4 className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                                    <Activity className="h-3.5 w-3.5" /> Recent Actions
                                  </h4>
                                  {!job.events?.length ? (
                                    <div className="rounded-lg border border-dashed border-divider p-4 text-sm text-muted-foreground">
                                      No actions recorded for this job.
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      {job.events.map((event: any) => (
                                        <div key={event.id} className="flex items-start gap-3 rounded-lg border border-divider bg-surface-subtle p-3">
                                          <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70" />
                                          <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                              <Badge variant="outline" className="text-[9px] py-0 bg-surface-subtle border-divider">
                                                {event.type}
                                              </Badge>
                                              <Badge variant="outline" className="text-[9px] py-0 bg-surface-subtle border-divider">
                                                {event.actorType}
                                              </Badge>
                                              <span className="text-[10px] text-muted-foreground">{new Date(event.createdAt).toLocaleString()}</span>
                                            </div>
                                            <p className="mt-1 break-all text-[11px] text-muted-foreground">actor: {event.actorId}</p>
                                            {event.payload && (
                                              <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-[10px] text-muted-foreground">
                                                {JSON.stringify(event.payload, null, 2)}
                                              </pre>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <div className="flex items-center gap-3">
                                  <Link to={`/jobs/${job.id}`} className="text-xs font-medium text-primary hover:underline">
                                    Open job detail
                                  </Link>
                                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                    <Gavel className="h-3 w-3" /> {job._count?.bids ?? 0} bids
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
